<template>
  <ionic-vue-page v-bind:title="screenTitle" v-bind:refresh="refresh">
    <ion-grid>
      <ion-row>
        <ion-col size="12">
          <ion-item button="true" color="dark" lines="none">
            <ion-label>Filter by month</ion-label>
            <ion-datetime
              v-on:ionChange="updatePayments"
              display-format="MMM YYYY"
              picker-format="MMM YYYY"
              value
              v-bind:min="minDate"
              v-bind:max="maxDate"
            ></ion-datetime>
            <font-awesome-icon slot="end" icon="calendar-alt"></font-awesome-icon>
          </ion-item>
        </ion-col>
      </ion-row>
    </ion-grid>

    <error-message v-if="error" v-bind:message="error"></error-message>

    <skeleton-list v-if="payments.length === 0"></skeleton-list>

    <monthly-pay-breakdown
      v-else
      v-for="(breakdown, index) in payments"
      v-bind:key="index"
      :breakdown="breakdown"
      :products="products"
    ></monthly-pay-breakdown>

    <ion-infinite-scroll v-on:ionInfinite="loadMore" threshold="100px" position="bottom">
      <ion-infinite-scroll-content></ion-infinite-scroll-content>
    </ion-infinite-scroll>
  </ionic-vue-page>
</template>

<script>
import paymentsApiService from "./../services/PaymentsApi";
import productsApiService from "./../services/ProductsApi";
import MonthlyPayBreakdown from "./../components/MonthlyPayBreakdown";
import SkeletonList from "./../components/SkeletonList";
import moment from "moment";
import mixins from "./../mixins/mixins";

const paramDateFormat = "YYYY-MM-DD";

export default {
  name: "Earnings",
  mixins: [mixins],
  components: {
    "monthly-pay-breakdown": MonthlyPayBreakdown,
    "skeleton-list": SkeletonList
  },
  data() {
    return {
      screenTitle: "Earnings",
      startDate: moment()
        .subtract(6, "months")
        .format(paramDateFormat),
      endDate: moment().format(paramDateFormat),
      payments: [],
      products: [],
      error: false,
      minDate: "2019-01-01",
      maxDate: moment()
        .endOf("month")
        .format(paramDateFormat)
    };
  },

  async mounted() {
    const loading = await this.$ionic.loadingController.create({
      message: "Loading monthly earnings..."
    });
    loading.present();

    try {
      const screenData = await this.getData();

      this.products = screenData[0];
      this.payments = this.handlePaymentsData(screenData[1]);
    } catch (error) {
      this.error = error;
    } finally {
      loading.dismiss();
    }
  },

  methods: {
    async loadMore(event) {
      this.formatDateRangeInfinite();

      if (
        this.startDate === "2019-01-01" &&
        moment(this.endDate).isBefore("2019-01-01")
      ) {
        event.target.disabled = true;
        return;
      }

      try {
        const newData = await paymentsApiService.getMonthly(
          this.startDate,
          this.endDate
        );

        const formattedData = this.handlePaymentsData(newData);

        for (let i = 0; i < formattedData.length; i++) {
          let paymentsLength = this.payments.length;
          this.$set(this.payments, paymentsLength, formattedData[i]);
        }
      } catch (error) {
        this.error = error;
      } finally {
        event.target.complete();
      }
    },

    formatDateRangeInfinite() {
      this.endDate = moment(this.startDate, paramDateFormat)
        .subtract(1, "month")
        .format(paramDateFormat);
      this.startDate = moment(this.endDate, paramDateFormat)
        .subtract(6, "month")
        .format(paramDateFormat);

      if (moment(this.startDate).isBefore("2019-01-01")) {
        this.startDate = "2019-01-01";
      }
    },

    async updatePayments(event) {
      const loading = await this.$ionic.loadingController.create({
        message: "Updating monthly earnings..."
      });
      loading.present();

      this.startDate = moment(event.target.value)
        .startOf("month")
        .format(paramDateFormat);
      this.endDate = moment(this.startDate)
        .endOf("month")
        .format(paramDateFormat);

      try {
        const paymentData = await paymentsApiService.getMonthly(
          this.startDate,
          this.endDate
        );

        this.payments = this.handlePaymentsData(paymentData);
      } catch (error) {
        this.error = error;
      } finally {
        loading.dismiss();
      }
    },

    async refresh(event) {
      this.payments = [];
      this.startDate = moment()
        .subtract(6, "months")
        .format(paramDateFormat);
      this.endDate = moment().format(paramDateFormat);

      try {
        const screenData = await this.getData();

        this.products = screenData[0];
        this.payments = this.handlePaymentsData(screenData[1]);
      } catch (error) {
        this.error = error;
      } finally {
        event.target.complete();
      }
    },

    async getData() {
      const productsCall = productsApiService.get();
      const paymentsCall = paymentsApiService.getMonthly(
        this.startDate,
        this.endDate
      );
      return await Promise.all([productsCall, paymentsCall]);
    }
  }
};
</script>